import { Component, Prop, h, Element } from '@stencil/core';

/**
 * `me-heading` is a semantic heading component that allows customizable
 * variant, alignment, and HTML tag rendering (e.g. h1–h6 or div).
 */

const COMPONENT_NAME = 'me-heading';
const DEFAULTS = {
  align: 'left',
} as const;

/**
 * @example
 *   <me-heading variant="2">Accelerate your workflow</me-heading>
 * @slot - {required}, {content}, string
 */
@Component({
  tag: 'me-heading',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Heading {
  /** Reference to the host element (<me-heading>) */
  @Element() host: HTMLElement;

  /**
   * The HTML tag used to render the heading.
   * This determines semantic importance (h1–h6) or neutral (`div`).
   * @default "div"
   */
  @Prop() tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' = 'div';

  /**
   * Visual variant of the heading.
   * Typically corresponds to font size, spacing, and visual hierarchy (1–6).
   * This value is used to generate a CSS modifier class.
   */
  @Prop() variant: '1' | '2' | '3' | '4' | '5' | '6';

  /**
   * Text alignment of the heading: left, center, or right.
   * @default "left"
   */
  @Prop() align: 'left' | 'right' | 'center' = DEFAULTS.align;

  render() {
    const Tag = this.tag;

    // Apply variant class if a value is provided
    const variantClass = this.variant ? `${COMPONENT_NAME}--${this.variant}` : '';

    // Apply alignment class only if it's different from the default
    const alignClass = this.align !== DEFAULTS.align ? `${COMPONENT_NAME}--${this.align}` : '';

    // Build final list of classes, omitting falsy values
    const classList = [COMPONENT_NAME, variantClass, alignClass].filter(Boolean).join(' ');

    return (
      <Tag class={classList}>
        <slot></slot>
      </Tag>
    );
  }
}
